import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, Paper, Typography, Card, CardContent, CardActionArea,
  CircularProgress, Drawer, Table, TableBody, TableCell, TableHead,
  TableRow, IconButton, Divider, Chip,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
  Inventory as InventoryIcon,
  Pets as PetsIcon,
  Receipt as ReceiptIcon,
  Close as CloseIcon,
  TrendingUp as TrendingIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';

interface DashboardStats {
  today_appointments: number;
  total_clients: number;
  total_patients: number;
  active_queue_entries: number;
  low_stock_items: number;
  monthly_revenue: number;
  pending_invoices_count: number;
  pending_invoices_total: number;
}

type DrawerType = 'revenue' | 'low-stock' | 'invoices' | null;

const DRAWER_TITLES: Record<NonNullable<DrawerType>, string> = {
  revenue: 'Monthly Revenue Breakdown',
  'low-stock': 'Low Stock Items',
  invoices: 'Pending Invoices',
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    today_appointments: 0, total_clients: 0, total_patients: 0,
    active_queue_entries: 0, low_stock_items: 0, monthly_revenue: 0,
    pending_invoices_count: 0, pending_invoices_total: 0,
  });
  const [drawer, setDrawer] = useState<DrawerType>(null);
  const [drawerData, setDrawerData] = useState<any[]>([]);
  const [drawerLoading, setDrawerLoading] = useState(false);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await api.get(API_ENDPOINTS.DASHBOARD.OVERVIEW);
        const s: DashboardStats = response?.stats ?? response ?? {};
        setStats({
          today_appointments: s.today_appointments ?? 0,
          total_clients: s.total_clients ?? 0,
          total_patients: s.total_patients ?? 0,
          active_queue_entries: s.active_queue_entries ?? 0,
          low_stock_items: s.low_stock_items ?? 0,
          monthly_revenue: s.monthly_revenue ?? 0,
          pending_invoices_count: s.pending_invoices_count ?? 0,
          pending_invoices_total: s.pending_invoices_total ?? 0,
        });
      } catch (error) {
        console.error('Failed to fetch dashboard:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const openDrawer = async (type: DrawerType) => {
    setDrawer(type);
    setDrawerLoading(true);
    setDrawerData([]);
    try {
      if (type === 'revenue') {
        const res = await api.get(API_ENDPOINTS.DASHBOARD.REVENUE);
        setDrawerData(res.by_payment_method ?? res.by_day ?? []);
      } else if (type === 'low-stock') {
        const res = await api.get(API_ENDPOINTS.INVENTORY.LOW_STOCK);
        setDrawerData(res.data ?? (Array.isArray(res) ? res : []));
      } else if (type === 'invoices') {
        const res = await api.get(`${API_ENDPOINTS.INVOICING.INVOICES}?payment_status=unpaid`);
        setDrawerData(res.data ?? []);
      }
    } catch {
      setDrawerData([]);
    } finally {
      setDrawerLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Appointments Today',
      value: stats.today_appointments,
      icon: <CalendarIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      onClick: () => navigate('/appointments'),
      hint: 'View today\'s schedule',
    },
    {
      title: 'Total Clients',
      value: stats.total_clients,
      icon: <PeopleIcon sx={{ fontSize: 40, color: 'info.main' }} />,
      onClick: () => navigate('/clients'),
      hint: 'View all clients',
    },
    {
      title: 'Total Patients',
      value: stats.total_patients,
      icon: <PetsIcon sx={{ fontSize: 40, color: 'secondary.main' }} />,
      onClick: () => navigate('/patients'),
      hint: 'View all patients',
    },
    {
      title: 'Queue (Active)',
      value: stats.active_queue_entries,
      icon: <PeopleIcon sx={{ fontSize: 40, color: 'warning.main' }} />,
      onClick: () => navigate('/queue'),
      hint: 'View queue',
    },
    {
      title: 'Monthly Revenue',
      value: `$${(stats.monthly_revenue ?? 0).toFixed(2)}`,
      icon: <MoneyIcon sx={{ fontSize: 40, color: 'success.main' }} />,
      onClick: () => openDrawer('revenue'),
      hint: 'View breakdown',
    },
    {
      title: 'Low Stock Items',
      value: stats.low_stock_items,
      icon: <InventoryIcon sx={{ fontSize: 40, color: 'error.main' }} />,
      onClick: () => openDrawer('low-stock'),
      hint: 'View items',
    },
    {
      title: 'Pending Invoices',
      value: stats.pending_invoices_count,
      icon: <ReceiptIcon sx={{ fontSize: 40, color: 'warning.main' }} />,
      onClick: () => openDrawer('invoices'),
      hint: 'View invoices',
    },
    {
      title: 'Pending Invoice Total',
      value: `$${(stats.pending_invoices_total ?? 0).toFixed(2)}`,
      icon: <MoneyIcon sx={{ fontSize: 40, color: 'error.main' }} />,
      onClick: () => openDrawer('invoices'),
      hint: 'View invoices',
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Dashboard</Typography>

      <Grid container spacing={3}>
        {statCards.map((stat) => (
          <Grid item xs={12} sm={6} md={3} key={stat.title}>
            <Card sx={{ '&:hover': { boxShadow: 4 }, transition: 'box-shadow 0.2s' }}>
              <CardActionArea onClick={stat.onClick} sx={{ height: '100%' }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        {stat.title}
                      </Typography>
                      <Typography variant="h5">{stat.value}</Typography>
                      <Typography variant="caption" color="primary">{stat.hint}</Typography>
                    </Box>
                    {stat.icon}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box mt={4}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Welcome to Veterinary Clinic SaaS</Typography>
          <Typography variant="body1" color="textSecondary">
            Your dashboard provides a live overview of today's activity, financials, and operational status.
          </Typography>
        </Paper>
      </Box>

      {/* Drill-down Drawer */}
      <Drawer anchor="right" open={!!drawer} onClose={() => setDrawer(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{drawer ? DRAWER_TITLES[drawer] : ''}</Typography>
          <IconButton onClick={() => setDrawer(null)}><CloseIcon /></IconButton>
        </Box>
        <Divider />
        <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
          {drawerLoading ? (
            <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
          ) : drawerData.length === 0 ? (
            <Typography color="textSecondary" align="center" sx={{ mt: 4 }}>No data found</Typography>
          ) : drawer === 'revenue' ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Payment Method</TableCell>
                  <TableCell align="right">Count</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {drawerData.map((row: any, i: number) => (
                  <TableRow key={i}>
                    <TableCell sx={{ textTransform: 'capitalize' }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <TrendingIcon fontSize="small" color="success" />
                        {String(row.payment_method ?? row.date ?? '').replace('_', ' ')}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{row.count ?? '—'}</TableCell>
                    <TableCell align="right"><strong>${Number(row.total ?? row.revenue ?? 0).toFixed(2)}</strong></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : drawer === 'low-stock' ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell align="right">Stock</TableCell>
                  <TableCell align="right">Min</TableCell>
                  <TableCell align="right">Shortage</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {drawerData.map((row: any) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <WarningIcon fontSize="small" color="error" />
                        <Box>
                          <Typography variant="body2">{row.name}</Typography>
                          {row.category && <Typography variant="caption" color="textSecondary">{row.category}</Typography>}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Chip label={row.current_stock} color="error" size="small" />
                    </TableCell>
                    <TableCell align="right">{row.min_stock_level ?? '—'}</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main', fontWeight: 700 }}>
                      {row.shortage != null ? `-${row.shortage}` : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : drawer === 'invoices' ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {drawerData.map((row: any) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Typography variant="body2"><strong>{row.invoice_number ?? `#${row.id}`}</strong></Typography>
                      <Typography variant="caption" color="textSecondary">
                        {row.created_at ? new Date(row.created_at).toLocaleDateString() : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.client_name ?? '—'}</TableCell>
                    <TableCell>
                      <Chip label={row.payment_status ?? 'unpaid'} color="warning" size="small"
                        sx={{ textTransform: 'capitalize' }} />
                    </TableCell>
                    <TableCell align="right"><strong>${Number(row.total_amount ?? 0).toFixed(2)}</strong></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </Box>
      </Drawer>
    </Box>
  );
};

export default DashboardPage;
